import { useMemo, useState } from 'react';
import { useMatch, useResolvedPath } from 'react-router-dom';

import { useIdentity } from 'SRC/hooks/useIdentity';
import { permissions } from 'SRC/permissions';

import { SidebarChildren } from './SidebarChildren';
import { SidebarMainItem } from './SidebarMainItem';
import { KnownLink, SidebarLinkProps } from './types';
import { linkTo } from '../../RoutesMain/routing';
import styles from '../AppLayout.module.scss';

const isKnownLink = (to: string): to is KnownLink => {
  return to in linkTo;
};

export function AppSidebarLink({
  to,
  label,
  icon,
  iconActive = icon,
  requiredPermission,
  childrenLinks,
  children,
  isChild,
  ...rest
}: SidebarLinkProps) {
  const { canDo } = useIdentity();

  const href = to && (isKnownLink(to) ? linkTo[to]() : to);

  const isEnabled = useMemo(() => {
    return requiredPermission ? canDo(permissions[requiredPermission]) : true;
  }, [requiredPermission, canDo]);

  const resolved = useResolvedPath(href || '');
  const match = useMatch({ path: resolved.pathname, end: to === 'root' });
  const isActive = !!match;

  const [isOpen, setIsOpen] = useState(isActive);

  const hasChildrenLinks = useMemo(
    () => (childrenLinks?.length ?? 0) > 0,
    [childrenLinks],
  );

  return (
    <>
      <SidebarMainItem
        href={hasChildrenLinks ? undefined : href}
        label={label}
        icon={icon}
        iconActive={iconActive}
        isActive={isActive}
        isDisabled={!isEnabled}
        isChild={isChild}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        className={isChild ? styles.sidebarLinkChild : undefined}
        {...rest}
      >
        {children}
      </SidebarMainItem>
      {hasChildrenLinks && isOpen && (
        <SidebarChildren childrenLinks={childrenLinks} />
      )}
    </>
  );
}
